import { defineLocalStore } from '@/utils/localStore'
import { computed, reactive, shallowRef } from 'vue'
import { useRoute } from 'vue-router'
import { handleAsync, setSnackbar, confirm } from 'best-modules/plugins'
import {
  docTypeClassList,
  docTypeDefinitionSingle,
  docTypeDefinitionUpdate,
  docTypeDefinitionActivate,
  docTypeDefinitionDeactivate,
  docTypeAccessDelete,
  docTypeFieldList,
  docTypeFieldDelete,
  docTypeFieldUpdate,
  docTypeFieldReorder,
} from '@/requests/documentType'
import { required, useVuelidate } from '@/plugins'
import { cachedObject } from 'best-modules/utils'
import {
  DocumentTypeDefinitionCredentials,
  DocumentTypeDefinition,
} from '@/utils/types/documentType'
import { DefaultItem } from 'best-modules/plugins/directory/types'
import { pick } from 'lodash'
import { initRoutes } from '@/components/doc-type-routes/routes'
import { openDialog } from '@/dialog'
import { DocumentTypeField } from '@/utils/types/documentTypeField'

export const { initLocalStore: initDocType, useLocalStore: useDocType } =
  defineLocalStore('docType', () => {
    const route = useRoute()
    const docTypeId = computed<number>(() => {
      return Number(route.params.id)
    })
    const docTypeSource = reactive<DocumentTypeDefinition>(
      {} as DocumentTypeDefinition
    )
    const docTypeCredentials = cachedObject<DocumentTypeDefinitionCredentials>({
      name: null,
      code: null,
      registrationMarkType: null,
      defaultRegistrationTypeId: null,
      defaultAccessLevelTypeId: null,
      description: null,
      docTypeClassId: null,
      isApprovalRequired: false,
      isSigningRequired: false,
      isApprovalEcpRequired: false,
      isSigningEcpRequired: false,
    } as DocumentTypeDefinitionCredentials)
    const docTypeClasses = shallowRef<DefaultItem[]>([])

    const setDocType = (obj: DocumentTypeDefinition): void => {
      Object.assign(docTypeSource, obj)
      docTypeCredentials.$set(obj)
    }

    const rules = {
      name: { required },
      code: { required },
    }
    const v$ = useVuelidate(rules, docTypeCredentials)

    const getDocType = () => {
      return handleAsync('getDocType', async () => {
        const data = await docTypeDefinitionSingle(docTypeId.value)

        setDocType(data)
      })
    }

    const getDocTypeClassList = () => {
      if (!docTypeClasses.value.length) {
        return docTypeClassList().then(res => {
          docTypeClasses.value = res.data.map(c => pick(c, ['id', 'name']))
        })
      }
    }

    const updateDocType = () => {
      v$.value.$touch()
      if (!v$.value.$invalid) {
        return docTypeDefinitionUpdate(
          docTypeId.value,
          docTypeCredentials.$object
        ).then(res => {
          setDocType(res)
          setSnackbar({ text: 'Дані успішно оновлені' })
        })
      }
    }

    const docTypeToggleActive = async () => {
      if (docTypeSource.isActive) {
        await docTypeDefinitionDeactivate(docTypeId.value)
      } else {
        await docTypeDefinitionActivate(docTypeId.value)
      }
      docTypeSource.isActive = !docTypeSource.isActive
    }

    const accessCreate = () => {
      openDialog({
        name: 'DocTypeAccess',
        action: 'create',
        dialogData: { docTypeId: docTypeId.value },
        params: {
          cb: (res: DocumentTypeDefinition) => {
            setDocType(res)
          },
        },
      })
    }

    const accessDelete = (accessId: number) => {
      confirm({ text: 'Видалити доступ?' }).then(() => {
        docTypeAccessDelete(accessId).then(() => {
          docTypeSource.accesses = docTypeSource.accesses.filter(
            a => a.id !== accessId
          )
        })
      })
    }

    // Fields
    const fields = shallowRef<DocumentTypeField[]>([])

    const getFields = () => {
      return docTypeFieldList(docTypeId.value).then(res => {
        fields.value = res
      })
    }

    const getExistFieldIds = () => fields.value.map(f => f.fieldDefinitionId)

    const fieldAdd = () => {
      openDialog({
        name: 'DocTypeField',
        action: 'create',
        dialogData: {
          docTypeId: docTypeId.value,
          existFieldIds: getExistFieldIds(),
        },
        params: {
          submitLabel: 'Створити поле',
          cb: res => {
            fields.value = [...fields.value, res]
            setSnackbar({ text: 'Поле додано' })
          },
        },
      })
    }

    const fieldDelete = (fieldId: number) => {
      return confirm({ text: 'Видалити поле?' }).then(() => {
        return docTypeFieldDelete(docTypeId.value, fieldId).then(() => {
          fields.value = fields.value.filter(f => f.id !== fieldId)
        })
      })
    }

    const fieldUpdate = (field: DocumentTypeField) => {
      openDialog({
        name: 'DocTypeField',
        action: 'update',
        dialogData: {
          docTypeId: docTypeId.value,
          existFieldIds: getExistFieldIds(),
          field,
        },
        params: {
          submitLabel: 'Зберегти',
          cb: res => {
            fields.value = fields.value.map(f => (f.id === res.id ? res : f))
            setSnackbar({ text: 'Поле оновлено' })
          },
        },
      })
    }

    const fieldChangeRenderSize = (
      field: DocumentTypeField,
      renderSize: DocumentTypeField['renderSize']
    ) => {
      return docTypeFieldUpdate(docTypeId.value, field.id, {
        renderSize,
      }).then(res => {
        fields.value = fields.value.map(f => (f.id === res.id ? res : f))
      })
    }

    const fieldUpdateSortOrder = (fieldId: number, sortOrder: number) => {
      return docTypeFieldReorder(docTypeId.value, {
        id: fieldId,
        sortOrder,
      }).then(res => {
        fields.value = res
      })
    }

    getDocType()
    getFields()
    initRoutes(docTypeId.value)

    return {
      docTypeId,
      docTypeSource,
      docTypeCredentials,
      v$,
      updateDocType,
      docTypeClasses,
      getDocTypeClassList,
      docTypeToggleActive,

      accessCreate,
      accessDelete,

      fields,
      fieldAdd,
      fieldUpdate,
      fieldDelete,
      fieldChangeRenderSize,
      fieldUpdateSortOrder,
    }
  })
